/**
 * Contrato de mensagens entre o Portal do Gerador embutido (guest) e o portal
 * multi-tenant que o embute (host).
 *
 * ESTE ARQUIVO TEM UM GÊMEO em `portal-multi-tenant`, em
 * `features/gerador-embed/src/protocol.ts`. Os dois precisam ser idênticos —
 * mudou aqui, muda lá, e suba o `EMBED_PROTOCOL_VERSION` se a mudança quebrar
 * a versão anterior. Não é pacote npm de propósito: o contrato é pequeno e
 * publicar um pacote só para ele custaria mais do que a duplicação.
 *
 * Por que postMessage e não controle direto do iframe: host e guest estão em
 * origens diferentes (`pereira.musa.co` × `portal-gerador.musa.co`). Mesmo
 * sendo same-site, cross-origin impede ler `contentWindow.location` ou injetar
 * CSS. `document.domain` foi removido do Chrome 115. Sobra postMessage.
 */

export const EMBED_PROTOCOL = "musa-portal-embed";

/** Sobe quando uma mudança quebra a compatibilidade com a versão anterior. */
export const EMBED_PROTOCOL_VERSION = 1;

/** Item do menu lateral, já filtrado pelas permissões do usuário. */
export interface EmbedNavItem {
  /** Rota interna do Portal do Gerador. Ex.: "/coletas". */
  id: string;
  label: string;
  /** Nome do ícone no catálogo do host, não o componente (não é serializável). */
  icon?: string;
}

export interface EmbedNavGroup {
  id: string;
  /** Sem label o grupo é renderizado sem título. */
  label?: string;
  items: EmbedNavItem[];
}

/** guest -> host */
export type GuestMessage =
  | { type: "ready" }
  | { type: "nav"; groups: EmbedNavGroup[] }
  | { type: "route"; path: string }
  /**
   * O botão de recolher menu vive no header do guest, mas o menu é do host.
   * Sem este recado ele seria um botão morto na tela.
   */
  | { type: "toggle-sidebar" }
  /**
   * `login_required` = o Keycloak precisa do formulário, que manda
   * `X-Frame-Options: SAMEORIGIN` e não renderiza dentro do iframe. Quem
   * navega para lá é o host, na janela de cima — o guest não consegue: navegar
   * o top a partir de um iframe cross-origin exige gesto do usuário, e o boot
   * não tem nenhum.
   */
  | { type: "auth"; state: "authenticated" | "login_required" };

/** host -> guest */
export type HostMessage = { type: "navigate"; path: string };

interface Envelope {
  protocol: typeof EMBED_PROTOCOL;
  version: number;
  role: "guest" | "host";
}

export type GuestEnvelope = Envelope & { role: "guest" } & GuestMessage;
export type HostEnvelope = Envelope & { role: "host" } & HostMessage;

export function packGuest(message: GuestMessage): GuestEnvelope {
  return { protocol: EMBED_PROTOCOL, version: EMBED_PROTOCOL_VERSION, role: "guest", ...message };
}

export function packHost(message: HostMessage): HostEnvelope {
  return { protocol: EMBED_PROTOCOL, version: EMBED_PROTOCOL_VERSION, role: "host", ...message };
}

/**
 * Aceita só o que veio do outro lado do contrato, na versão certa.
 *
 * A janela recebe mensagem de extensão de browser, de widget de suporte e de
 * qualquer iframe de terceiro. Sem esta peneira, qualquer um deles conseguiria
 * mandar o portal navegar.
 */
function unpack<T>(data: unknown, role: "guest" | "host"): T | null {
  if (typeof data !== "object" || data === null) return null;
  const envelope = data as Partial<Envelope>;
  if (envelope.protocol !== EMBED_PROTOCOL) return null;
  if (envelope.version !== EMBED_PROTOCOL_VERSION) return null;
  if (envelope.role !== role) return null;
  return data as T;
}

export const unpackGuest = (data: unknown) => unpack<GuestEnvelope>(data, "guest");
export const unpackHost = (data: unknown) => unpack<HostEnvelope>(data, "host");
